// Friendly labels for network / crypto vulnerability IDs emitted by
// qtz-discovery-cli. The authoritative list is `networkVulnIDs` in the CLI's
// internal/output/cyclonedx.go — that is the set routed into the CycloneDX
// vulnerabilities[] array the backend ingests. Keep in sync when new IDs are
// added on the CLI side.
//
// An unknown ID is not a failure: formatVuln falls back to the raw ID, so a
// missing entry costs a readable label, not the finding.

export interface VulnLabel {
  label: string
  description: string
  category:
    | 'protocol'
    | 'cipher'
    | 'certificate'
    | 'ocsp'
    | 'extension'
    | 'pqc'
    | 'auth'
    | 'database'
    | 'vpn'
    | 'dns'
    | 'ot'
}

const LABELS: Record<string, VulnLabel> = {
  // ── TLS protocol versions ────────────────────────────────────────────
  SSL2_ENABLED: {
    label: 'SSL 2.0 enabled',
    description: 'Broken by design — no handshake integrity, weak MAC. RFC 6176 forbids SSLv2.',
    category: 'protocol',
  },
  SSL3_ENABLED: {
    label: 'SSL 3.0 enabled',
    description: 'POODLE-vulnerable. RFC 7568 forbids SSLv3.',
    category: 'protocol',
  },
  TLS10_ENABLED: {
    label: 'TLS 1.0 enabled',
    description: 'BEAST / POODLE risk. Deprecated by RFC 8996.',
    category: 'protocol',
  },
  TLS11_ENABLED: {
    label: 'TLS 1.1 enabled',
    description: 'Deprecated by RFC 8996. No AEAD, weak MAC.',
    category: 'protocol',
  },
  NO_TLS13: {
    label: 'No TLS 1.3',
    description: 'TLS 1.3 not offered; misses AEAD-only ciphers and 1-RTT handshake.',
    category: 'protocol',
  },

  // ── Cipher / key exchange ────────────────────────────────────────────
  DEPRECATED_CIPHER: {
    label: 'Deprecated cipher',
    description: 'RC4, DES, NULL, or EXPORT cipher negotiated.',
    category: 'cipher',
  },
  NO_PFS: {
    label: 'No Perfect Forward Secrecy',
    description: 'Session keys derivable from long-term private key if compromised.',
    category: 'cipher',
  },
  // The two *_ACCEPTED ids come from cipher-suite enumeration: the server did
  // not pick these for the scan's own connection, but it will accept them from
  // a client that asks. A weak suite that is never negotiated is still reachable.
  WEAK_CIPHER_ACCEPTED: {
    label: 'Weak cipher accepted',
    description: 'Server accepts RC4, DES, NULL or EXPORT when offered, though it did not negotiate one here.',
    category: 'cipher',
  },
  NO_PFS_ACCEPTED: {
    label: 'Non-PFS cipher accepted',
    description: 'Server accepts a static-key suite when offered, so a client can opt out of forward secrecy.',
    category: 'cipher',
  },
  DHE_DETECTED: {
    label: 'Classical DHE',
    description: 'DHE group size unverifiable — Logjam risk (RFC 7919).',
    category: 'cipher',
  },
  AES128_DETECTED: {
    label: 'AES-128 (below CNSA 2.0)',
    description: 'CNSA 2.0 requires AES-256 for national security systems.',
    category: 'cipher',
  },
  LOGJAM_WEAK_DH: {
    label: 'Weak DH parameters (Logjam)',
    description: 'DH group size ≤ 1024 bits — vulnerable to Logjam (CVE-2015-4000).',
    category: 'cipher',
  },
  EXPORT_CIPHER_OFFERED: {
    label: 'Export-grade cipher offered',
    description: 'FREAK-class weakness (CVE-2015-0204).',
    category: 'cipher',
  },

  // ── Certificate trust ────────────────────────────────────────────────
  CERT_EXPIRED: {
    label: 'Expired certificate',
    description: 'notAfter is in the past. Renew immediately.',
    category: 'certificate',
  },
  CERT_SELF_SIGNED: {
    label: 'Self-signed certificate',
    description: 'Issuer equals subject; no CA chain-of-trust.',
    category: 'certificate',
  },
  CERT_HOSTNAME_MISMATCH: {
    label: 'Hostname mismatch',
    description: 'Certificate SANs do not include the scan target hostname (RFC 6125).',
    category: 'certificate',
  },
  CERT_UNTRUSTED: {
    label: 'Untrusted certificate',
    description: 'Chain failed verification (unknown CA, invalid constraints, or path error).',
    category: 'certificate',
  },
  CERT_WEAK_SIG: {
    label: 'Weak signature algorithm',
    description: 'Certificate signed with MD5 or SHA-1 — collision-vulnerable.',
    category: 'certificate',
  },
  CERT_WEAK_KEY: {
    label: 'Weak certificate key',
    description: 'RSA key below 2048 bits — factorable within adversary budget.',
    category: 'certificate',
  },

  // ── OCSP / revocation ────────────────────────────────────────────────
  OCSP_NOT_STAPLED: {
    label: 'OCSP not stapled',
    description: 'Advisory: enables faster revocation checking.',
    category: 'ocsp',
  },
  OCSP_REVOKED: {
    label: 'Certificate revoked',
    description: 'OCSP staple confirms the cert has been revoked.',
    category: 'ocsp',
  },
  OCSP_STALE: {
    label: 'OCSP staple stale',
    description: 'Cached OCSP response is past its NextUpdate.',
    category: 'ocsp',
  },

  // ── Extensions / negotiation ─────────────────────────────────────────
  HEARTBEAT_EXT_NEGOTIATED: {
    label: 'Heartbeat extension enabled',
    description: 'OpenSSL 1.0.1–1.0.1f: Heartbleed (CVE-2014-0160).',
    category: 'extension',
  },
  TLS_COMPRESSION_OFFERED: {
    label: 'TLS compression offered',
    description: 'CRIME attack (CVE-2012-4929). Disable client + server.',
    category: 'extension',
  },
  INSECURE_RENEGOTIATION: {
    label: 'Insecure renegotiation',
    description: 'Missing renegotiation_info extension (CVE-2009-3555).',
    category: 'extension',
  },
  FALLBACK_SCSV_MISSING: {
    label: 'TLS_FALLBACK_SCSV missing',
    description: 'Enables version-rollback attacks (POODLE-class).',
    category: 'extension',
  },

  // ── SSH ──────────────────────────────────────────────────────────────
  SSH_WEAK_HOST_KEY: {
    label: 'Weak SSH host key',
    description: 'ssh-rsa or ssh-dss host key algorithm.',
    category: 'protocol',
  },
  SSH_NO_PQC_KEX: {
    label: 'No PQC SSH KEX',
    description: 'Server does not offer mlkem768x25519-sha256 hybrid KEX.',
    category: 'pqc',
  },
  SSH_OUTDATED_SERVER: {
    label: 'Outdated SSH server',
    description: 'Banner reports a superseded OpenSSH version.',
    category: 'protocol',
  },
  // ── Post-quantum key exchange ────────────────────────────────────────
  PQC_DECLINED_BY_SERVER: {
    label: 'Server declined PQC key exchange',
    description:
      'The client offered a PQC or hybrid group and the server negotiated a classical one. ' +
      'A server configuration gap, not a client limitation.',
    category: 'pqc',
  },

  // ── SSH ──────────────────────────────────────────────────────────────
  SSH_WEAK_KEX: {
    label: 'Weak SSH key exchange',
    description: 'Server accepts a key-exchange algorithm no longer considered safe.',
    category: 'cipher',
  },
  SSH_WEAK_MAC: {
    label: 'Weak SSH MAC',
    description: 'Server accepts a MAC algorithm no longer considered safe.',
    category: 'cipher',
  },

  // ── Plaintext credentials ────────────────────────────────────────────
  HTTP_BASIC_AUTH_PLAINTEXT: {
    label: 'HTTP Basic auth in plaintext',
    description: 'Credentials are base64, not encrypted, and were observed unprotected.',
    category: 'auth',
  },
  FTP_PLAINTEXT_CREDS: {
    label: 'FTP credentials in plaintext',
    description: 'FTP USER/PASS observed unencrypted. Use FTPS or SFTP.',
    category: 'auth',
  },
  TELNET_PLAINTEXT_CREDS: {
    label: 'Telnet credentials in plaintext',
    description: 'Telnet transmits everything unencrypted. Use SSH.',
    category: 'auth',
  },
  IMAP_LOGIN_PLAINTEXT: {
    label: 'IMAP LOGIN in plaintext',
    description: 'IMAP LOGIN observed before any STARTTLS upgrade.',
    category: 'auth',
  },
  POP3_PLAINTEXT_CREDS: {
    label: 'POP3 credentials in plaintext',
    description: 'POP3 USER/PASS observed unencrypted.',
    category: 'auth',
  },
  SMTP_AUTH_PLAIN_PLAINTEXT: {
    label: 'SMTP AUTH PLAIN in plaintext',
    description: 'SMTP AUTH PLAIN observed before any STARTTLS upgrade.',
    category: 'auth',
  },
  LDAP_SIMPLE_BIND_PLAINTEXT: {
    label: 'LDAP simple bind in plaintext',
    description: 'LDAP simple bind sends the password unencrypted. Use LDAPS or StartTLS.',
    category: 'auth',
  },

  // ── Tokens and session auth ──────────────────────────────────────────
  JWT_ALG_NONE: {
    label: 'JWT with alg=none',
    description: 'An unsigned JWT was accepted — any party can forge a token.',
    category: 'auth',
  },
  JWT_WEAK_HS256: {
    label: 'JWT signed with weak HS256 key',
    description: 'HMAC-SHA256 with a short or guessable secret is brute-forceable offline.',
    category: 'auth',
  },
  NTLM_AUTH_OBSERVED: {
    label: 'NTLM authentication observed',
    description: 'NTLM is relay- and cracking-prone. Prefer Kerberos.',
    category: 'auth',
  },
  KERBEROS_NO_PREAUTH: {
    label: 'Kerberos pre-authentication disabled',
    description: 'Allows AS-REP roasting: an attacker can request crackable material offline.',
    category: 'auth',
  },
  KERBEROS_PQC_PKINIT_DETECTED: {
    label: 'Kerberos PKINIT with PQC',
    description: 'PKINIT negotiated a post-quantum algorithm.',
    category: 'pqc',
  },
  WPA_PSK_HANDSHAKE_CAPTURED: {
    label: 'WPA PSK handshake captured',
    description: 'A full 4-way handshake was observed and is crackable offline.',
    category: 'auth',
  },
  EAP_MD5: {
    label: 'EAP-MD5 in use',
    description: 'Offers no mutual authentication and no key material. Deprecated.',
    category: 'auth',
  },
  EAP_MSCHAPV2: {
    label: 'EAP-MSCHAPv2 in use',
    description: 'Reducible to DES; recoverable offline. Tunnel inside TLS or replace.',
    category: 'auth',
  },

  // ── SNMP ─────────────────────────────────────────────────────────────
  SNMP_V1_COMMUNITY: {
    label: 'SNMPv1 community string',
    description: 'SNMPv1 sends the community string in clear. Use SNMPv3.',
    category: 'auth',
  },
  SNMP_V2C_COMMUNITY: {
    label: 'SNMPv2c community string',
    description: 'SNMPv2c sends the community string in clear. Use SNMPv3.',
    category: 'auth',
  },
  SNMP_DEFAULT_COMMUNITY: {
    label: 'Default SNMP community string',
    description: 'A default community such as "public" or "private" is in use.',
    category: 'auth',
  },

  // ── Databases ────────────────────────────────────────────────────────
  DB_POSTGRES_MD5_AUTH: {
    label: 'PostgreSQL MD5 authentication',
    description: 'MD5 auth is deprecated and offline-crackable. Use scram-sha-256.',
    category: 'database',
  },
  DB_MYSQL_NO_TLS: {
    label: 'MySQL connection without TLS',
    description: 'Queries and results travel in clear.',
    category: 'database',
  },
  DB_MONGO_SCRAM_SHA1: {
    label: 'MongoDB SCRAM-SHA-1',
    description: 'SHA-1-based auth. Use SCRAM-SHA-256.',
    category: 'database',
  },
  DB_MONGO_LEGACY_CR_AUTH: {
    label: 'MongoDB legacy CR authentication',
    description: 'MONGODB-CR was removed in MongoDB 4.0 and is MD5-based.',
    category: 'database',
  },
  DB_MONGO_PLAINTEXT_TRAFFIC: {
    label: 'MongoDB traffic in plaintext',
    description: 'Wire protocol observed without TLS.',
    category: 'database',
  },
  DB_REDIS_AUTH_PLAINTEXT: {
    label: 'Redis AUTH in plaintext',
    description: 'The Redis password was observed unencrypted.',
    category: 'database',
  },
  DB_MSSQL_PLAINTEXT_LOGIN: {
    label: 'SQL Server login in plaintext',
    description: 'TDS login packet observed without encryption.',
    category: 'database',
  },
  DB_ORACLE_PLAINTEXT_TNS: {
    label: 'Oracle TNS in plaintext',
    description: 'TNS traffic observed without native encryption or TLS.',
    category: 'database',
  },
  DB_CASSANDRA_PLAINTEXT: {
    label: 'Cassandra traffic in plaintext',
    description: 'CQL traffic observed without client encryption.',
    category: 'database',
  },
  DB_CLICKHOUSE_PLAINTEXT: {
    label: 'ClickHouse traffic in plaintext',
    description: 'Native or HTTP interface observed without TLS.',
    category: 'database',
  },
  DB_MEMCACHED_NO_AUTH: {
    label: 'Memcached without authentication',
    description: 'No SASL authentication — anyone reachable can read and write.',
    category: 'database',
  },

  // ── SMB / RDP ────────────────────────────────────────────────────────
  SMB1_ENABLED: {
    label: 'SMBv1 enabled',
    description: 'SMBv1 is deprecated and the EternalBlue vector. Disable it.',
    category: 'protocol',
  },
  SMB_SIGNING_DISABLED: {
    label: 'SMB signing disabled',
    description: 'Permits relay and tampering of SMB sessions.',
    category: 'protocol',
  },
  RDP_STANDARD_SECURITY: {
    label: 'RDP standard security',
    description: 'Legacy RDP security is vulnerable to man-in-the-middle. Require TLS.',
    category: 'protocol',
  },
  RDP_NLA_DISABLED: {
    label: 'RDP without Network Level Authentication',
    description: 'Sessions are established before authentication.',
    category: 'protocol',
  },

  // ── VPN ──────────────────────────────────────────────────────────────
  PPTP_DETECTED: {
    label: 'PPTP VPN detected',
    description: 'PPTP is cryptographically broken. Replace it.',
    category: 'vpn',
  },
  MSCHAPV2_DETECTED: {
    label: 'MS-CHAPv2 detected',
    description: 'Reducible to a single DES key and recoverable offline.',
    category: 'vpn',
  },
  OPENVPN_DETECTED: {
    label: 'OpenVPN detected',
    description: 'Informational: an OpenVPN tunnel was observed.',
    category: 'vpn',
  },
  WIREGUARD_DETECTED: {
    label: 'WireGuard detected',
    description: 'Informational: a WireGuard tunnel was observed. No PQC key exchange today.',
    category: 'vpn',
  },

  // ── DNS ──────────────────────────────────────────────────────────────
  DNSSEC_WEAK_ALGORITHM: {
    label: 'Weak DNSSEC algorithm',
    description: 'A zone is signed with a deprecated algorithm such as RSA/SHA-1.',
    category: 'dns',
  },
  DNSSEC_WEAK_KEY: {
    label: 'Weak DNSSEC key',
    description: 'A DNSSEC key is below the recommended size.',
    category: 'dns',
  },
  DOT_DETECTED: {
    label: 'DNS over TLS detected',
    description: 'Informational: DoT traffic was observed.',
    category: 'dns',
  },
  DOH_DETECTED: {
    label: 'DNS over HTTPS detected',
    description: 'Informational: DoH traffic was observed.',
    category: 'dns',
  },
  DOQ_DETECTED: {
    label: 'DNS over QUIC detected',
    description: 'Informational: DoQ traffic was observed.',
    category: 'dns',
  },

  // ── Operational technology ───────────────────────────────────────────
  OT_UNENCRYPTED_MODBUS: {
    label: 'Modbus without encryption',
    description: 'Modbus/TCP has no authentication or encryption. Segment the network.',
    category: 'ot',
  },
  OT_UNENCRYPTED_DNP3: {
    label: 'DNP3 without encryption',
    description: 'DNP3 observed without Secure Authentication.',
    category: 'ot',
  },
  OT_UNENCRYPTED_S7: {
    label: 'S7comm without encryption',
    description: 'Siemens S7 traffic observed in clear.',
    category: 'ot',
  },

  // ── Cleartext application protocols ──────────────────────────────────
  H2C_PLAINTEXT: {
    label: 'HTTP/2 cleartext (h2c)',
    description: 'HTTP/2 observed without TLS.',
    category: 'protocol',
  },
  GRPC_PLAINTEXT: {
    label: 'gRPC without TLS',
    description: 'gRPC observed over cleartext HTTP/2.',
    category: 'protocol',
  },
}

// formatVuln returns friendly metadata for a vulnerability ID. Unknown
// IDs pass through with the raw string as label so newly-added CLI IDs
// still render (no silent drop).
export function formatVuln(id: string): VulnLabel {
  return (
    LABELS[id] ?? {
      label: id,
      description: '',
      category: 'protocol',
    }
  )
}

// isCertVuln returns true when the ID belongs to the certificate-trust
// bucket. Used for inventory summary counts.
export function isCertVuln(id: string): boolean {
  const info = LABELS[id]
  return info?.category === 'certificate' || info?.category === 'ocsp'
}

// isPQCRelevant flags IDs that map to a PQC-migration decision:
// cert replacement, KEX/signature upgrade, deprecated primitive removal.
export function isPQCRelevant(id: string): boolean {
  const info = LABELS[id]
  if (!info) return false
  return (
    info.category === 'pqc' ||
    info.category === 'certificate' ||
    id === 'NO_TLS13' ||
    id === 'AES128_DETECTED' ||
    id === 'DHE_DETECTED'
  )
}
